import { useCallback, useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import EmailList from "./components/EmailList";
import EmailView from "./components/EmailView";
import TempMailGen from "./components/TempMailGen";
import ComposeModal from "./components/ComposeModal";
import Login from "./components/Login";
import { Icon } from "./components/Icons";
import { api } from "./services/api";

const emptyInbox = { items: [], pagination: { page: 1, pages: 1, total: 0, limit: 20 } };

export default function App() {
  const [authenticated, setAuthenticated] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [theme, setTheme] = useState(() => normalizeTheme(localStorage.getItem("wwmaxik-theme")));
  const [section, setSection] = useState("inbox");
  const [inbox, setInbox] = useState(emptyInbox);
  const [tempInbox, setTempInbox] = useState(emptyInbox);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewLoading, setViewLoading] = useState(false);
  const [compose, setCompose] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const loadInbox = useCallback(async ({ targetPage = page, search = query, quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      setInbox(await api.getInbox({ page: targetPage, query: search }));
    } catch (error) {
      if (error.status === 401) setAuthenticated(false);
      else notify(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [page, query, notify]);

  const loadTempInbox = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      setTempInbox(await api.getInbox({ temp: true }));
    } catch (error) {
      if (error.status === 401) setAuthenticated(false);
      else notify(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    api.getSession().then(() => setAuthenticated(true)).catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("wwmaxik-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!authenticated) return;
    api.getTheme().then((result) => setTheme(result.theme)).catch((error) => notify(error.message, "error"));
  }, [authenticated, notify]);

  useEffect(() => {
    if (!authenticated) return undefined;
    const timer = window.setTimeout(() => {
      setPage(1);
      loadInbox({ targetPage: 1, search: query });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [authenticated, query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authenticated && section === "temp") loadTempInbox();
  }, [authenticated, section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authenticated || section !== "temp") return undefined;
    const timer = window.setInterval(() => loadTempInbox({ quiet: true }), 10_000);
    return () => window.clearInterval(timer);
  }, [authenticated, section, loadTempInbox]);

  async function openEmail(id) {
    setViewLoading(true);
    setSelected({ id });
    try {
      setSelected(await api.getEmail(id));
      if (section === "temp") setTempInbox((current) => markRead(current, id));
      else setInbox((current) => markRead(current, id));
    } catch (error) {
      setSelected(null);
      notify(error.message, "error");
    } finally {
      setViewLoading(false);
    }
  }

  async function deleteEmail(id) {
    if (!window.confirm("Удалить письмо без возможности восстановления?")) return;
    try {
      await api.deleteEmail(id);
      setSelected(null);
      section === "temp" ? loadTempInbox() : loadInbox();
      notify("Письмо удалено");
    } catch (error) {
      notify(error.message, "error");
    }
  }

  if (authenticated === null) return <div className="splash"><span className="spinner" /></div>;
  if (!authenticated) {
    return <Login error={loginError} onLogin={async (password) => {
      setLoginError("");
      try {
        await api.login(password);
        setAuthenticated(true);
      } catch (error) {
        setLoginError(error.message);
      }
    }} />;
  }

  const changeSection = (next) => {
    setSection(next);
    setSelected(null);
    if (next === "temp") loadTempInbox();
    else loadInbox({ targetPage: page, search: query });
  };

  async function changeTheme(nextTheme) {
    const previous = theme;
    setTheme(nextTheme);
    try {
      await api.updateTheme(nextTheme);
    } catch (error) {
      setTheme(previous);
      notify(error.message, "error");
    }
  }
  const listProps = {
    loading,
    onSelect: openEmail,
    onRefresh: () => loadInbox(),
    onPage: (next) => { setPage(next); loadInbox({ targetPage: next }); },
  };

  return (
    <div className="appShell">
      <Sidebar
        section={section}
        theme={theme}
        onTheme={changeTheme}
        onNavigate={changeSection}
        onCompose={() => setCompose(true)}
        onLogout={async () => { await api.logout(); setAuthenticated(false); }}
        open={mobileMenu}
        onClose={() => setMobileMenu(false)}
      />
      <main className="mainContent">
        <button className="mobileMenuButton" onClick={() => setMobileMenu(true)}><Icon name="menu" /> WWMaxik Mail</button>
        {selected ? (
          <EmailView email={selected} loading={viewLoading} onBack={() => setSelected(null)} onDelete={deleteEmail} onDownload={async (file) => {
            try { await api.downloadAttachment(file.id, file.filename); }
            catch (error) { notify(error.message, "error"); }
          }} />
        ) : section === "inbox" ? (
          <EmailList {...listProps} data={inbox} query={query} onQuery={setQuery} />
        ) : (
          <TempMailGen inboxProps={{ ...listProps, data: tempInbox, onRefresh: loadTempInbox, onPage: () => {} }} />
        )}
      </main>
      <ComposeModal open={compose} onClose={() => setCompose(false)} onSend={async (form) => {
        try { await api.sendEmail(form); notify("Письмо отправлено"); }
        catch (error) { notify(error.message, "error"); throw error; }
      }} />
      {toast && <div className={`toast ${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
}

function markRead(data, id) {
  return { ...data, items: data.items.map((item) => item.id === id ? { ...item, isRead: true } : item) };
}

function normalizeTheme(value) {
  if (["red", "blue", "green"].includes(value)) return value;
  return { dark: "red", light: "blue", cyberpunk: "green" }[value] || "red";
}
